import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authClient, type AuthUser } from '@/lib/authClient'

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    let mounted = true
    authClient.getSession().then(({ data }) => {
      if (!mounted) return
      setUser((data?.user as AuthUser | undefined) ?? null)
      setLoading(false)
    })

    const unsubscribe = authClient.$store.listen('$sessionSignal', async () => {
      const { data } = await authClient.getSession()
      if (!mounted) return
      const next = (data?.user as AuthUser | undefined) ?? null
      if (!next) {
        queryClient.clear()
      } else if (next.id) {
        queryClient.invalidateQueries({ queryKey: ['userProfile', next.id] })
        queryClient.invalidateQueries({ queryKey: ['profile'] })
      }
      setUser(next)
    })

    return () => {
      mounted = false
      unsubscribe?.()
    }
  }, [queryClient])

  const signOut = async () => {
    queryClient.clear()
    await authClient.signOut()
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
